import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqejbozd';
export function Book() {
  const [status, setStatus] = useState<Status>('idle');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });
    setStatus('submitting');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        form.reset();
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  const resetForm = () => setStatus('idle');
  return <div className="w-full bg-white min-h-screen" data-id="element-255">
      <section className="py-12 md:py-20" data-id="element-256">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-257">
          <div className="flex flex-col md:flex-row gap-12 lg:gap-20" data-id="element-258">
            {/* Info Column */}
            <div className="flex-1" data-id="element-259">
              <h1 className="font-bebas text-5xl md:text-7xl mb-6 tracking-wide" data-id="element-260">
                Book Your
                <br data-id="element-261" />
                <span className="text-reach-red" data-id="element-262">Consultation</span>
              </h1>
              <p className="font-inter text-lg text-reach-black mb-10" data-id="element-263">
                Take the first step towards elite performance. Fill out the form
                to schedule a free facility tour and athlete evaluation.
              </p>

              <div className="space-y-6" data-id="element-264">
                <div className="flex items-start gap-4" data-id="element-265">
                  <div className="w-12 h-12 bg-reach-gray rounded-full flex items-center justify-center shrink-0" data-id="element-266">
                    <Calendar className="text-reach-red" size={24} data-id="element-267" />
                  </div>
                  <div data-id="element-268">
                    <h3 className="font-bebas text-2xl tracking-wide mb-1" data-id="element-269">
                      What to expect
                    </h3>
                    <p className="font-inter text-sm text-gray-600" data-id="element-270">
                      A 30-minute walkthrough of INPOWERiQ and a discussion of
                      your athlete's specific goals.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-271">
                  <div className="w-12 h-12 bg-reach-gray rounded-full flex items-center justify-center shrink-0" data-id="element-272">
                    <Clock className="text-reach-red" size={24} data-id="element-273" />
                  </div>
                  <div data-id="element-274">
                    <h3 className="font-bebas text-2xl tracking-wide mb-1" data-id="element-275">
                      Flexible Scheduling
                    </h3>
                    <p className="font-inter text-sm text-gray-600" data-id="element-276">
                      We'll contact you within 24 hours to find a time that
                      works for your family's schedule.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="flex-1" data-id="element-277">
              <div className="bg-reach-gray p-8 rounded-lg border-t-4 border-reach-red" data-id="element-278">
                {status === 'success' ? <div className="text-center py-12" data-id="element-279">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-280">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-281">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-282" />
                      </svg>
                    </div>
                    <h3 className="font-bebas text-3xl tracking-wide mb-4" data-id="element-283">
                      Thanks, your message is in
                    </h3>
                    <p className="font-inter text-reach-black mb-6" data-id="element-284">
                      Coach Fernando typically responds within 24 hours.
                    </p>
                    <button type="button" onClick={resetForm} className="inline-flex items-center justify-center font-inter font-semibold text-sm uppercase tracking-wider px-5 py-2.5 border-2 border-reach-black text-reach-black rounded hover:bg-reach-black hover:text-white transition-colors" data-id="element-285">
                      Send another
                    </button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-286">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-287">
                      <div data-id="element-288">
                        <label htmlFor="parentName" className="block font-inter text-sm font-semibold mb-2" data-id="element-289">
                          Parent/Guardian Name
                        </label>
                        <div className="relative" data-id="element-290">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-291">
                            <User size={18} className="text-gray-400" data-id="element-292" />
                          </div>
                          <input type="text" id="parentName" name="parentName" required className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter" placeholder="John Doe" data-id="element-293" />
                        </div>
                      </div>
                      <div data-id="element-294">
                        <label htmlFor="athleteName" className="block font-inter text-sm font-semibold mb-2" data-id="element-295">
                          Athlete Name
                        </label>
                        <div className="relative" data-id="element-296">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-297">
                            <User size={18} className="text-gray-400" data-id="element-298" />
                          </div>
                          <input type="text" id="athleteName" name="athleteName" required className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter" placeholder="Jane Doe" data-id="element-299" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-300">
                      <div data-id="element-301">
                        <label htmlFor="email" className="block font-inter text-sm font-semibold mb-2" data-id="element-302">
                          Email Address
                        </label>
                        <div className="relative" data-id="element-303">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-304">
                            <Mail size={18} className="text-gray-400" data-id="element-305" />
                          </div>
                          <input type="email" id="email" name="email" required className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter" placeholder="john@example.com" data-id="element-306" />
                        </div>
                      </div>
                      <div data-id="element-307">
                        <label htmlFor="phone" className="block font-inter text-sm font-semibold mb-2" data-id="element-308">
                          Phone Number
                        </label>
                        <div className="relative" data-id="element-309">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-310">
                            <Phone size={18} className="text-gray-400" data-id="element-311" />
                          </div>
                          <input type="tel" id="phone" name="phone" required className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter" placeholder="(555) 123-4567" data-id="element-312" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-313">
                      <div data-id="element-314">
                        <label htmlFor="age" className="block font-inter text-sm font-semibold mb-2" data-id="element-315">
                          Athlete Age
                        </label>
                        <select id="age" name="age" required className="w-full px-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter" data-id="element-316">
                          <option value="" data-id="element-317">Select age</option>
                          <option value="8-11" data-id="element-318">8-11 (Youth)</option>
                          <option value="12-14" data-id="element-319">12-14 (Middle School)</option>
                          <option value="15-18" data-id="element-320">15-18 (High School)</option>
                          <option value="18+" data-id="element-321">18+ (College/Pro)</option>
                          <option value="pro-collegiate" data-id="element-322">
                            Pro / Collegiate
                          </option>
                        </select>
                      </div>
                      <div data-id="element-323">
                        <label htmlFor="sport" className="block font-inter text-sm font-semibold mb-2" data-id="element-324">
                          Primary Sport
                        </label>
                        <input type="text" id="sport" name="sport" className="w-full px-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter" placeholder="e.g. Football, Soccer" data-id="element-325" />
                      </div>
                    </div>

                    <div data-id="element-326">
                      <label htmlFor="goals" className="block font-inter text-sm font-semibold mb-2" data-id="element-327">
                        Goals / Notes
                      </label>
                      <div className="relative" data-id="element-328">
                        <div className="absolute top-3 left-3 pointer-events-none" data-id="element-329">
                          <MessageSquare size={18} className="text-gray-400" data-id="element-330" />
                        </div>
                        <textarea id="goals" name="goals" rows={4} className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter resize-none" placeholder="What are you looking to improve?" data-id="element-331"></textarea>
                      </div>
                    </div>

                    <button type="submit" disabled={status === 'submitting'} className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-332">
                      {status === 'submitting' ? 'Sending' : 'Submit Request'}
                    </button>

                    {status === 'error' && <p className="font-inter text-sm text-red-600 text-center" data-id="element-333">
                        Something went wrong. Please email
                        Fernando@reachperformancehq.com or call 708-980-4666.
                      </p>}
                  </form>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}